import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom'; // Import useParams and useNavigate
import { AuthContext } from '../../context/AuthContext'; // Import AuthContext
import '../../styles/ComprehensionResults.scss';
import ComprehensionView from './ComprehensionView'; // Import ComprehensionView
import ComprehensionUpload from './ComprehensionUpload';
import logger from '../../utils/logger'; // Import logger
import { track } from '../../utils/analytics';

// ---- Copied Helper Functions from ResultAndRevision ----

// Helper function to check if it's a GCSE category
function isGCSECategory(category) {
    // Assuming category might be null or undefined sometimes
    if (!category) return false;
    return category.startsWith('GCSE_');
}

/**
 * processInlineFeedback(text, category)
 * Converts inline annotations into colored spans and block feedback into styled divs.
 */
function processInlineFeedback(text, category) {
    logger.debug("--- processInlineFeedback START ---");
    logger.debug("Original text input type:", typeof text);
    logger.debug("Original text input value:", text);

    // Initial check for null/undefined/empty string
    if (!text) {
        logger.debug("Input text is empty, null, or undefined. Returning empty string.");
        logger.debug("--- processInlineFeedback END ---");
        return '';
    }

    // Explicitly check if the input is a string AFTER the initial check
    if (typeof text !== 'string') {
        logger.warn("processInlineFeedback received non-string input after initial check:", text, "(Type:", typeof text, ")");
        // Return empty string or perhaps an error indicator?
        return '<span style="color: red;">[Invalid Feedback Format]</span>';
    }

    // 1. Detect and store if [TEACHER] tag exists (case-insensitive, trim)
    const teacherTagRegex = /\[\s*TEACHER\s*\]/i;
    const isTeacherFeedback = teacherTagRegex.test(text);
    const teacherBadgeHTML = isTeacherFeedback ? '<span class="teacher-badge small">Teacher</span>' : '';
    logger.debug("Detected isTeacherFeedback:", isTeacherFeedback);

    // 2. Clean the text by removing the tag globally (case-insensitive)
    let cleanedText = text.replace(teacherTagRegex, '').trim();
    logger.debug("Cleaned text:", cleanedText);

    // 3. Apply formatting to the cleaned text
    let processed = cleanedText.replace(/\n{3,}/g, '\n\n');

    // Define color maps and code sets (as before)
    const colorMapEnglish = { 'P': '#2ecc71', 'C': '#e74c3c', 'L': '#3498db', 'M': '#f1c40f' };
    const colorMapGCSE = { 'A': '#9b59b6', 'L': '#3498db', 'C': '#f39c12', 'M': '#e74c3c' };
    let colorMap = colorMapEnglish;
    let codeSet = '[PCLM]';
    if (isGCSECategory(category)) {
        colorMap = colorMapGCSE;
        codeSet = '[ALCM]|AO\\d+'; // Support both single letters and AO codes
    }

    let formatted = false; // Flag to track if formatting was applied

    // Apply {{...}} formatting
    const doubleBracketRegex = new RegExp(`\\{\\{(${codeSet}):\\s*([^}]+?)\\}\\}`, 'g');
    processed = processed.replace(doubleBracketRegex, (match, type, content) => {
        logger.debug(`Applying {{...}} format for type: ${type}`);
        formatted = true;
        return `<span style="background-color: ${colorMap[type]}33; border-bottom: 2px solid ${colorMap[type]};">${content.trim()}</span>`;
    });

    // Apply {...} formatting
    const singleBracketRegex = new RegExp(`\\{(${codeSet}):\\s*(.*?)\\}`, 'g');
    processed = processed.replace(singleBracketRegex, (match, type, content) => {
        logger.debug(`Applying {...} format for type: ${type}`);
        formatted = true;
        const displayContent = content.trim();
        return `<div class="feedback-block" style="margin: 5px 0; padding: 8px; background-color: ${colorMap[type]}22; border-left: 4px solid ${colorMap[type]};">${teacherBadgeHTML}${displayContent}</div>`;
    });
    logger.debug("Processed text after regex:", processed);

    // 4. Handle plain text: If it was teacher feedback AND wasn't formatted by the regex above
    if (isTeacherFeedback && !formatted) {
        logger.debug("Handling as plain teacher text.");
        processed = `<div class="feedback-block teacher-plain">${teacherBadgeHTML}${processed}</div>`;
    }

    logger.debug("Final returned HTML:", processed);
    logger.debug("--- processInlineFeedback END ---");
    return processed;
}

// ---- End Copied Helper Functions ----

const ComprehensionResults = ({ API_BASE_URL, authToken: propAuthToken, userData: propUserData, isCreating, setIsCreating = () => {}, initialSubmissionData, onBack }) => {
  const { submissionId: urlSubmissionId } = useParams();
  const { authToken: contextAuthToken, API_BASE_URL: contextApiBaseUrlFromContext, userData: contextUserData } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const authToken = contextAuthToken || propAuthToken;
  const effectiveApiBaseUrl = contextApiBaseUrlFromContext || API_BASE_URL || process.env.REACT_APP_API_BASE_URL;
  const userData = contextUserData || propUserData;

  // State for submission data (either from prop or fetched)
  const [submissionData, setSubmissionData] = useState(initialSubmissionData || null);
  const [isLoading, setIsLoading] = useState(!initialSubmissionData && !!urlSubmissionId);
  const [error, setError] = useState(null);

  // --- Teacher-specific state (only relevant if userData.role === 'teacher') ---
  const [teacherComprehensions, setTeacherComprehensions] = useState([]);
  const [publicComprehensions, setPublicComprehensions] = useState([]);
  const [submissionsForSelected, setSubmissionsForSelected] = useState([]);
  const [selectedCompForSubmissions, setSelectedCompForSubmissions] = useState(null);
  const [viewLevel, setViewLevel] = useState('listComprehensions'); // Default for teacher
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [submissionsError, setSubmissionsError] = useState(null);
  const [editingFeedback, setEditingFeedback] = useState(null);
  const [feedbackEditText, setFeedbackEditText] = useState('');
  const [feedbackEditType, setFeedbackEditType] = useState('P');
  const [feedbackUpdateStatus, setFeedbackUpdateStatus] = useState(null);
  const [editingScore, setEditingScore] = useState(null);
  const [scoreEditValue, setScoreEditValue] = useState('');
  const [teacherLoading, setTeacherLoading] = useState(false); // Separate loading for teacher data
  const [teacherError, setTeacherError] = useState(null); // Separate error for teacher data
  const [isTeacherAnswering, setIsTeacherAnswering] = useState(false); // NEW: Track if teacher is answering
  const [comprehensionToAnswer, setComprehensionToAnswer] = useState(null); // NEW: Store the comprehension being answered
  // --- End Teacher-specific state ---

  // Local fallback for create mode when parent doesn't supply props
  const [localIsCreating, setLocalIsCreating] = useState(false);
  const isCreatingEffective = typeof isCreating === 'boolean' ? isCreating : localIsCreating;
  const setIsCreatingEffective = (value) => {
    if (typeof setIsCreating === 'function' && typeof isCreating === 'boolean') {
      setIsCreating(value);
    } else {
      setLocalIsCreating(value);
    }
  };

  // Effect to handle initialSubmissionData prop (overrides URL fetch if provided)
  useEffect(() => {
    if (initialSubmissionData) {
      logger.debug("Setting submission data from initial prop");
      setSubmissionData(initialSubmissionData);
      setIsLoading(false);
      setError(null);
    }
  }, [initialSubmissionData]);

  // Effect to fetch submission by ID from URL (if no initial data)
  useEffect(() => {
    // If we have initial data, we are definitely not loading from URL
    if (initialSubmissionData) {
      logger.debug("Using initialSubmissionData, setting isLoading=false");
      setSubmissionData(initialSubmissionData);
      setIsLoading(false);
      setError(null);
      return; // Early exit, nothing more to do
    }

    // If we have a URL ID, attempt to fetch
    if (urlSubmissionId) {
      const fetchSubmission = async () => {
        logger.info(`Attempting fetch for submission ID: ${urlSubmissionId}`);
        // No need to set loading here, it's handled before call
        setError(null);
        try {
          const response = await fetch(`${effectiveApiBaseUrl}/api/submissions/${urlSubmissionId}/details`, {
            headers: {
              'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`,
            },
          });

          logger.debug(`Fetch response status for ${urlSubmissionId}: ${response.status}`);

          if (!response.ok) {
            if (response.status === 403) {
              logger.warn(`Access Denied (403) fetching submission ${urlSubmissionId}`);
              throw new Error('Access Denied. You may not have permission to view this submission.');
            }
            const errorData = await response.json().catch(() => ({ error: 'Failed to parse error response' }));
            logger.error(`Failed to fetch submission ${urlSubmissionId}: ${response.status} - ${errorData.error || response.statusText}`);
            throw new Error(errorData.error || `Failed to fetch submission: ${response.statusText}`);
          }
          const data = await response.json();
          logger.info(`Successfully fetched submission details for ID: ${urlSubmissionId}`);
          setSubmissionData(data);
        } catch (err) {
          logger.error('Caught error during fetchSubmission execution:', err);
          setError(err.message || 'An error occurred while fetching submission details.');
        } finally {
          logger.debug(`fetchSubmission finally block reached for ${urlSubmissionId}, setting isLoading=false`);
          setIsLoading(false); // Reset loading regardless of outcome
        }
      };

      // Check if auth details are ready *now*
      if (authToken && effectiveApiBaseUrl) {
        logger.info(`Auth token present, starting fetch for ${urlSubmissionId}`);
        setIsLoading(true); // Set loading *only* when we actually start fetching
        fetchSubmission();
      } else {
        // Auth token not ready yet.
        // Initial state isLoading is true, so we just wait.
        // Log a debug message.
        logger.warn(`Auth token not yet available for ${urlSubmissionId} fetch, waiting... Current token state: ${authToken ? 'Exists' : 'Missing'}`);
        // IMPORTANT: If the token *never* arrives, isLoading will remain true. 
        // Consider adding a timeout mechanism if this becomes a persistent issue.
      }
    }
    // If no URL ID and no initial data, we are not loading.
    else {
      logger.debug("No urlSubmissionId and no initialData, setting isLoading=false");
      setIsLoading(false);
      setError(null); // No error if there's nothing to load
    }

  }, [urlSubmissionId, initialSubmissionData, authToken, effectiveApiBaseUrl]); // Dependencies

  // --- Teacher-specific useEffects --- 
  // Fetch teacher's comprehensions (Level 1)
  useEffect(() => {
    // Only run if the user is a teacher and we are not viewing/loading a specific submission
    if (userData?.role === 'teacher' && !submissionData && !isLoading && viewLevel === 'listComprehensions') {
        setTeacherLoading(true);
        setTeacherError(null);
        const fetchComprehensions = async () => {
           try {
             const response = await fetch(`${effectiveApiBaseUrl}/api/comprehensions/teacher`, {
               headers: {
                 'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`
               }
             });
             if (!response.ok) throw new Error('Failed to fetch teacher comprehensions data');
             const data = await response.json(); // Expect { teacher_comprehensions: [], public_comprehensions: [] }

             // Parse questions in teacher comprehensions
             const rawTeacherComps = data.teacher_comprehensions || [];
             const parsedAndSortedTeacherComps = rawTeacherComps.map(comp => ({
                 ...comp,
                 questions: typeof comp.questions === 'string' ? JSON.parse(comp.questions) : comp.questions
             })).sort((a, b) => new Date(a.created_at) - new Date(b.created_at)); // Sort by created_at ascending
             setTeacherComprehensions(parsedAndSortedTeacherComps);

             // Parse questions in public comprehensions
             const rawPublicComps = data.public_comprehensions || [];
             const parsedAndSortedPublicComps = rawPublicComps.map(comp => ({
                 ...comp,
                 questions: typeof comp.questions === 'string' ? JSON.parse(comp.questions) : comp.questions
             })).sort((a, b) => b.title.localeCompare(a.title)); // Sort by title descending
             setPublicComprehensions(parsedAndSortedPublicComps);

           } catch (err) {
             setTeacherError(err.message);
           } finally {
             setTeacherLoading(false);
           }
        };
        fetchComprehensions();
    }
  }, [effectiveApiBaseUrl, authToken, userData?.role, submissionData, isLoading, viewLevel, isCreatingEffective]); // Dependencies

  // Fetch submissions when a teacher selects a comprehension (Level 2)
  useEffect(() => {
    if (userData?.role === 'teacher' && viewLevel === 'listSubmissions' && selectedCompForSubmissions) {
      setSubmissionsLoading(true);
      setSubmissionsError(null);
      const fetchSubmissions = async () => {
        try {
          const response = await fetch(`${effectiveApiBaseUrl}/api/comprehensions/${selectedCompForSubmissions.id}/submissions`, {
            headers: {
              'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`
            }
          });
          if (!response.ok) {
              const errorData = await response.json();
              throw new Error(errorData.error || 'Failed to fetch submissions');
          }
          const data = await response.json();
          setSubmissionsForSelected(data);
        } catch (err) { setSubmissionsError(err.message); } 
        finally { setSubmissionsLoading(false); }
      };
      fetchSubmissions();
    } else { setSubmissionsForSelected([]); }
  }, [viewLevel, selectedCompForSubmissions, effectiveApiBaseUrl, authToken, userData?.role]);
  // --- End Teacher-specific useEffects ---

  // ---- Feedback Editing/Adding Handlers (modified for single submission view) ----
  const handleStartAddEditFeedback = (qIndex) => {
    // Feedback editing only makes sense when viewing a single submission
    if (!submissionData) return;

    const existingFeedbackItem = submissionData.feedback?.[qIndex];
    // Adjust based on actual feedback structure
    const existingFeedbackString = typeof existingFeedbackItem === 'string' ? existingFeedbackItem : existingFeedbackItem?.feedback ?? null;

    if (existingFeedbackString) {
        // Editing existing feedback
        const match = existingFeedbackString.match(/^\{([PCLMGSAE]):\s*(.*?)\}(\s*\[TEACHER\])?$/);
        const currentText = match ? match[2] : existingFeedbackString; // Fallback to raw string
        const currentType = match ? match[1] : 'P'; // Default to 'P' if parsing fails

        setEditingFeedback({ qIndex, currentType }); // No subIndex needed for single view
        setFeedbackEditText(currentText);
        setFeedbackEditType(currentType); // Set editor type to current
    } else {
        // Adding new feedback
        setEditingFeedback({ qIndex, currentType: null });
        setFeedbackEditText('');
        setFeedbackEditType('P'); // Default to Purpose/First type
    }
    setFeedbackUpdateStatus(null); // Clear any previous status
  };

  const handleSaveFeedback = async () => {
      if (!editingFeedback || !submissionData) return;
      const { qIndex, currentType } = editingFeedback;
      const submissionId = submissionData.id;
      const isNewFeedback = currentType === null;
      // Use the type selected in the editor if new, otherwise keep original type (unless editor allows type change)
      const finalFeedbackType = feedbackEditType; 

      if (!submissionId || !feedbackEditText.trim()) {
        logger.error("Cannot save feedback - missing submission ID or text");
        return;
      }

      setFeedbackUpdateStatus('saving');
      // Clean up any existing TEACHER tags to prevent duplicates
      const cleanedFeedbackText = feedbackEditText.trim().replace(/\s*\[TEACHER\]\s*/g, '').trim();
      const formattedFeedback = `{${finalFeedbackType}: ${cleanedFeedbackText}} [TEACHER]`;

      try {
        const response = await fetch(`${effectiveApiBaseUrl}/api/submissions/${submissionId}/feedback/${qIndex}`, {
          method: 'POST',
          headers: {
            'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ feedback_content: formattedFeedback })
        });

        if (!response.ok) {
          track('teacher_save_feedback_error', {
            submission_id: submissionData.id,
            question_index: qIndex,
            error: 'API Error'
          });
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to save feedback');
        }

        // Update local state optimistically
        setSubmissionData(prev => {
            if (!prev) return null;
            const newFeedback = [...(prev.feedback || [])];
            // Ensure feedback array is long enough
            while (newFeedback.length <= qIndex) { newFeedback.push(null); }
            // Update the specific feedback item (assuming feedback is stored directly as string or object)
            // Adjust this based on your backend structure. If it's an object: { feedback: "..." }
            // If it's just the string, replace directly:
            newFeedback[qIndex] = formattedFeedback; // Or { feedback: formattedFeedback } if needed
            return { ...prev, feedback: newFeedback };
        });

        track('teacher_save_feedback_success', {
          submission_id: submissionData.id,
          question_index: qIndex
        });
        handleCancelFeedbackEdit(); // Close editor on success
        setTimeout(() => setFeedbackUpdateStatus(null), 2000); // Clear success message

      } catch (err) {
        track('teacher_save_feedback_error', {
          submission_id: submissionData.id,
          question_index: qIndex,
          error: err.message
        });
        logger.error("Error saving feedback:", err);
        setFeedbackUpdateStatus('error');
        // Keep editor open on error for correction
      }
  };

  const handleCancelFeedbackEdit = () => {
    setEditingFeedback(null);
    setFeedbackEditText('');
    setFeedbackEditType('P'); // Reset type
    setFeedbackUpdateStatus(null);
  };

  // Function to get feedback type options (can be simplified or adjusted)
  const getFeedbackTypeOptions = () => {
    // Using standard English PCLM for comprehension for now
    return [
      { value: 'P', label: 'Purpose (P)', color: '#2ecc71' },
      { value: 'C', label: 'Coherence (C)', color: '#e74c3c' },
      { value: 'L', label: 'Language (L)', color: '#3498db' },
      { value: 'M', label: 'Mechanics (M)', color: '#f1c40f' }
    ];
  };

  // --- Event Handlers (mostly teacher-specific, keep them but ensure they don't conflict) ---
  const handleSelectComprehension = (comp) => {
    if (userData?.role !== 'teacher') return;
    track('teacher_view_comprehension_submissions', {
      comprehension_id: comp.id,
      comprehension_title: comp.title,
      submission_count: comp.student_submission_count
    });
    setSelectedCompForSubmissions(comp);
    setViewLevel('listSubmissions');
    setSubmissionData(null); // Clear any single submission data
  };

  const handleStartTeacherAnswer = (comp) => {
    if (userData?.role !== 'teacher') return;
    track('teacher_start_comprehension_answer', {
      comprehension_id: comp.id,
      comprehension_title: comp.title
    });
    setComprehensionToAnswer(comp);
    setIsTeacherAnswering(true);
    setSubmissionData(null);
    setViewLevel('teacherAnswering');
  };

  const handleTeacherSubmissionComplete = (completedSubmissionData) => {
    track('teacher_complete_comprehension_answer', {
      comprehension_id: completedSubmissionData.comprehension_id,
      submission_id: completedSubmissionData.id
    });
    setSubmissionData(completedSubmissionData);
    setIsTeacherAnswering(false);
    setComprehensionToAnswer(null);
    setViewLevel('viewSubmissionDetail');
    logger.info("Teacher submission complete, showing results.");
  };

  const handleTeacherAnswerBack = () => {
    setIsTeacherAnswering(false);
    setComprehensionToAnswer(null);
    setViewLevel('listComprehensions'); // Go back to the list
  };

  const handleSelectSubmission = (submission) => {
    if (userData?.role !== 'teacher') return;
    track('teacher_view_submission_detail', {
      submission_id: submission.id,
      student_name: submission.student_name || 'Unknown Student'
    });
    const fetchDetailsAndSetView = async (subId) => {
      setSubmissionsLoading(true);
      setError(null); // Use main error state for fetching details
      try {
        const detailsResponse = await fetch(`${effectiveApiBaseUrl}/api/submissions/${subId}/details`, { 
            headers: { 'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}` }
        });
        if (!detailsResponse.ok) {
           if (detailsResponse.status === 403) throw new Error('Access Denied.');
           throw new Error('Failed to fetch submission details');
        }
        const fullSubmissionData = await detailsResponse.json();
        setSubmissionData(fullSubmissionData);
        setViewLevel('viewSubmissionDetail'); // Indicate teacher is viewing detail
      } catch (err) {
        setError(err.message);
      } finally {
        setSubmissionsLoading(false);
      }
    };
    fetchDetailsAndSetView(submission.id);
  };

  const handleBackToList = () => {
    setSelectedCompForSubmissions(null);
    setSubmissionsForSelected([]);
    setViewLevel('listComprehensions');
    setSubmissionData(null); // Clear single submission data
  };

  const handleBackToSubmissionList = () => {
    setViewLevel('listSubmissions');
    setSubmissionData(null); // Clear single submission data
  };
  
  // Add handleStartEditScore function
  const handleStartEditScore = (type, index, currentScore) => {
    setEditingScore({ type, index });
    setScoreEditValue(currentScore?.toString() || '');
  };
  
  // ... (handleStartAddEditFeedback, handleSaveFeedback, handleCancelFeedbackEdit, getFeedbackTypeOptions)
  // Ensure these handlers correctly reference submissionData when called from the single view

  const handleSaveScore = async (score, questionIndex = null) => {
    if (!submissionData?.id) return;
    // Check if user is teacher
    if (userData?.role !== 'teacher') {
        logger.warn("Attempt to save score by non-teacher user.");
        return; 
    }
    
    try {
        const endpoint = questionIndex !== null 
            ? `${effectiveApiBaseUrl}/api/submissions/${submissionData.id}/score/${questionIndex}`
            : `${effectiveApiBaseUrl}/api/submissions/${submissionData.id}/score`;

        const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`
            },
            body: JSON.stringify({ score: parseFloat(score) })
        });

        if (!response.ok) throw new Error('Failed to update score');

        // Update local state
        setSubmissionData(prev => {
            if (!prev) return null;
            if (questionIndex !== null) {
                const newQuestionScores = [...(prev.question_scores || [])];
                // Ensure array is long enough
                while (newQuestionScores.length <= questionIndex) { newQuestionScores.push(null); }
                newQuestionScores[questionIndex] = parseFloat(score);
                return { ...prev, question_scores: newQuestionScores };
            } else {
                return { ...prev, score: parseFloat(score) };
            }
        });

        setEditingScore(null);
        setScoreEditValue('');

        track('teacher_save_score_success', {
          submission_id: submissionData.id,
          question_index: questionIndex,
          score: score
        });

    } catch (error) {
        track('teacher_save_score_error', {
          submission_id: submissionData.id,
          question_index: questionIndex,
          error: error.message
        });
        logger.error('Error saving score:', error);
        setError('Failed to save score. Please try again.');
    }
  };

  // --- START RENDERING LOGIC --- //

  // 1. Handle Loading State (when fetching specific submission by URL)
  if (isLoading) {
    return <div className="loading-container"><div className="loading-spinner"></div>Loading submission details...</div>;
  }

  // 2. Handle Error State (when fetching specific submission failed)
  if (error) {
    return <div className="error-message">Error: {error}</div>;
  }

  // 3. Render Submission Details (if data is available from prop or fetch)
  if (submissionData) {
      const submission = submissionData;
      // Safely access potentially missing data with defaults
      const questions = Array.isArray(submission.comprehension_questions) ? submission.comprehension_questions : [];
      const answers = Array.isArray(submission.answers) ? submission.answers : [];
      const feedback = Array.isArray(submission.feedback) ? submission.feedback : [];
      const questionScores = Array.isArray(submission.question_scores) ? submission.question_scores : [];
      const comprehensionTitle = submission.comprehension_title || 'Submission Result';

      // Determine back button behavior based on context
      let handleGoBack;
      if (initialSubmissionData && onBack) {
          handleGoBack = onBack; // Use provided callback if data came from prop
      } else if (userData?.role === 'teacher' && viewLevel === 'viewSubmissionDetail') {
          handleGoBack = handleBackToSubmissionList; // Teacher goes back to submission list
      } else {
          // For students, navigate back to their submissions list
          handleGoBack = () => navigate('/my-submissions');
      }

      return (
          <div className="comprehension-submissions-view styled-results single-submission">
              <button onClick={handleGoBack} className="back-button">
                  ← Back
              </button>
              <h2>{comprehensionTitle} - {userData?.role === 'teacher' ? `${submission.student_name || 'Unknown Student'}'s Result` : 'Your Result'}</h2>
              {(
                  <p className="disclaimer-note"><strong>Note: Pulc does not offer a grade for comprehensions right now. Teachers can manually set scores for each student submission.</strong> </p>
              )}
              <div className="submissions-list"> { /* Maybe rename class */} 
                  <div className="submission-details-card">
                      <div className="submission-meta">
                          {userData?.role === 'teacher' && 
                              <span className="student-name">Student: {submission.student_name || 'Unknown Student'}</span>
                          }
                          <span className="submission-date">Submitted: {submission.submitted_at ? new Date(submission.submitted_at).toLocaleString() : 'Unknown Date'}</span>
                      </div>
                      {/* Overall Score Section */}
                      <div className="grade-container mini-grade-box">
                          <h4 className="grade-title">Overall Score</h4>
                          {userData?.role === 'teacher' && editingScore?.type === 'overall' ? (
                              <div className="score-edit-container">
                                  <input type="number" min="0" value={scoreEditValue} onChange={(e) => setScoreEditValue(e.target.value)} className="score-input" />
                                  <div className="score-edit-actions">
                                      <button onClick={() => handleSaveScore(scoreEditValue)} className="save-score">Save</button>
                                      <button onClick={() => setEditingScore(null)} className="cancel-score">Cancel</button>
                                  </div>
                              </div>
                          ) : (
                              <div className="score-display">
                                  <p className="final-score">{submission.score !== null ? `${submission.score} marks` : 'Not scored'}</p>
                                  {userData?.role === 'teacher' && (
                                      <button onClick={() => handleStartEditScore('overall', null, submission.score)} className="edit-score-button" title="Edit overall score">✏️</button>
                                  )}
                              </div>
                          )}
                      </div>
                      {/* Q&A + Feedback Section */} 
                      <div className="qna-feedback-container">
                          <h3 className="analysis-title">Answers & Feedback</h3>
                          {questions.map((question, qIndex) => {
                              const answer = answers[qIndex] ?? '[No Answer Provided]';
                              const feedbackItem = feedback[qIndex] ?? null; // Feedback might be null or an object
                              // Adjust based on actual feedback structure - assuming it's just the string for now
                              const feedbackString = typeof feedbackItem === 'string' ? feedbackItem : feedbackItem?.feedback ?? null;
                              
                              const questionText = typeof question === 'string' ? question : question?.text ?? 'Unknown Question';
                              const questionPoints = typeof question === 'object' ? question?.points : null;
                              const isEditingThisFeedback = editingFeedback?.subIndex === 0 && editingFeedback?.qIndex === qIndex; // Simplified check for single view
                              const isEditingThisScore = editingScore?.type === 'question' && editingScore?.index === qIndex;
                              const questionScore = questionScores[qIndex];

                              return (
                                  <div key={qIndex} className="qna-item paragraph-container">
                                      <div className="question-header">
                                          <div className="question-display">
                                              <strong>Q{qIndex + 1}: {questionText}</strong>
                                              {questionPoints && <span className="points-display"> ({questionPoints} points)</span>}
                                          </div>
                                          <div className="question-score">
                                              {userData?.role === 'teacher' && isEditingThisScore ? (
                                                  <div className="score-edit-container">
                                                      <input type="number" min="0" value={scoreEditValue} onChange={(e) => setScoreEditValue(e.target.value)} className="score-input"/>
                                                      <div className="score-edit-actions">
                                                          <button onClick={() => handleSaveScore(scoreEditValue, qIndex)} className="save-score">Save</button>
                                                          <button onClick={() => setEditingScore(null)} className="cancel-score">Cancel</button>
                                                      </div>
                                                  </div>
                                              ) : (
                                                  <div className="score-display">
                                                      <span>Score: {questionScore !== null ? `${questionScore} marks` : 'Not scored'}</span>
                                                      {userData?.role === 'teacher' && (
                                                          <button onClick={() => handleStartEditScore('question', qIndex, questionScore)} className="edit-score-button" title="Edit question score">✏️</button>
                                                      )}
                                                  </div>
                                              )}
                                          </div>
                                      </div>
                                      <div className="answer-display paragraph-text">
                                          <p>{answer}</p>
                                      </div>
                                      <div className="feedback-display feedback-column">
                                          <strong>Feedback:</strong>
                                          {isEditingThisFeedback && userData?.role === 'teacher' ? (
                                            <div className="feedback-edit-container">
                                              {/* Feedback Editor UI (simplified example) */}
                                              <textarea 
                                                value={feedbackEditText}
                                                onChange={(e) => setFeedbackEditText(e.target.value)}
                                                rows={3}
                                              />
                                              {/* Type selector maybe */} 
                                              <button onClick={handleSaveFeedback}>Save</button>
                                              <button onClick={handleCancelFeedbackEdit}>Cancel</button>
                                              {feedbackUpdateStatus === 'saving' && <span> Saving...</span>}
                                              {feedbackUpdateStatus === 'error' && <span style={{color: 'red'}}> Error!</span>}
                                            </div>
                                          ) : (
                                              <>
                                                  {feedbackString !== null ? (
                                                      <div dangerouslySetInnerHTML={{ __html: processInlineFeedback(feedbackString, 'comprehension') }} />
                                                  ) : (
                                                      <p className="no-feedback">No feedback provided yet.</p>
                                                  )}
                                                  {userData?.role === 'teacher' && (
                                                      <button /* onClick={() => handleStartAddEditFeedback(0, qIndex)} */ className="add-edit-feedback-button small" title={feedbackString !== null ? "Edit Feedback" : "Add Feedback"}>
                                                          {feedbackString !== null ? '✏️ Edit' : '+ Add Feedback'}
                                                      </button>
                                                  )}
                                              </>
                                          )}
                                      </div>
                                  </div>
                              );
                          })}
                      </div>
                  </div>
              </div>
          </div>
      );
  }
  
  // 4. Handle Teacher-Specific Views (Comprehension List, Submission List)
  // These should only render if the user IS a teacher and no specific submission is loaded
  if (userData?.role === 'teacher') {
    // If teacher is in create mode, show creation form
    if (isCreatingEffective) {
      return (
        <div className="comprehension-selection-view teacher-variant">
          <button onClick={() => setIsCreatingEffective(false)} className="back-button">← Back to Comprehensions</button>
          <h2>Create New Comprehension</h2>
          <ComprehensionUpload 
            API_BASE_URL={effectiveApiBaseUrl}
            authToken={authToken}
            userData={userData}
            onBack={() => setIsCreatingEffective(false)}
          />
        </div>
      );
    }
    // 1. If teacher is viewing a specific submission (Level 3)
    if (viewLevel === 'viewSubmission' && submissionData) {
      return (
        <div className="comprehension-results max-w-5xl mx-auto p-4 md:p-6">
          <button onClick={handleBackToSubmissionList} className="back-button inline-flex items-center text-sm text-slate-600 hover:text-slate-900 hover:underline mb-3">← Back to Submissions</button>
          <h2 className="text-2xl font-semibold tracking-tight text-slate-900 mb-4">Submission Details</h2>
          {/* Reuse ComprehensionView for detailed display if needed, or custom details here */}
          {/* For now, display a simple summary */}
          <div className="submission-details rounded-lg border border-slate-200 bg-white p-4">
            <p className="text-slate-700"><strong>Student:</strong> {submissionData.student_name || 'Unknown Student'}</p>
            <p className="text-slate-700"><strong>Score:</strong> {submissionData.score !== null ? submissionData.score : 'Pending'}</p>
          </div>
        </div>
      );
    }
    if (viewLevel === 'teacherAnswering' && isTeacherAnswering && comprehensionToAnswer) {
      // Render ComprehensionView for the teacher to answer
      return (
        <div className="comprehension-results max-w-5xl mx-auto p-4 md:p-6">
          <button onClick={handleBackToList} className="back-button">← Back to Comprehensions</button>
          <h2>Answer Comprehension</h2>
          <ComprehensionView 
            API_BASE_URL={effectiveApiBaseUrl}
            authToken={authToken}
            userData={userData}
            initialComprehension={comprehensionToAnswer}
            onBack={handleBackToList}
            onSubmissionComplete={(submissionData) => {
              // After teacher answers, go back to list or show feedback
              setIsTeacherAnswering(false);
              setComprehensionToAnswer(null);
              setViewLevel('listComprehensions');
            }}
          />
        </div>
      );
    } else if (viewLevel === 'listSubmissions' && selectedCompForSubmissions) {
          // Render Teacher's Submission List View (Level 2)
          return (
              <div className="comprehension-submissions-view">
                  <button onClick={handleBackToList} className="back-button">← Back to Comprehensions</button>
                  <h2>{selectedCompForSubmissions.title} - Submissions</h2>
                  {submissionsLoading && <div className="loading">Loading submissions...</div>}
                  {submissionsError && <div className="error-message">Error: {submissionsError}</div>}
                  {!submissionsLoading && !submissionsError && (
                      submissionsForSelected.length === 0 ? (
                          <p className="no-submissions">No submissions yet for this comprehension.</p>
                      ) : (
                          <div className="submissions-grid">
                              {submissionsForSelected.map((sub) => (
                                  <div key={sub.id} className="submission-tile" onClick={() => handleSelectSubmission(sub)}>
                                      <h4>{sub.student_name || 'Unknown Student'}</h4>
                                      <p>Submitted: {new Date(sub.submitted_at).toLocaleDateString()}</p>
                                      {sub.score !== null && <p className="score">Score: {sub.score} marks</p>}
                                  </div>
                              ))}
                          </div>
                      )
                  )}
              </div>
          );
      } else if (viewLevel === 'listComprehensions') {
          // Render Teacher's Comprehension List View (Level 1) - MIMICKING STUDENT VIEW
          return (
            // Use similar container class as student view for layout
            <div className="comprehension-selection-view teacher-variant max-w-6xl mx-auto p-4 md:p-6"> 
                {/* Move button above the title and use the green style */}
                {!isCreatingEffective && (
             <div className="mb-4 flex justify-center">
               <button 
                 onClick={() => setIsCreatingEffective(true)} 
                 className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-emerald-500 to-green-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:shadow-md hover:from-emerald-600 hover:to-green-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2"
               >
                 <span>+ Create New Comprehension</span>
               </button>
             </div>
          )}
                <h2 className="text-2xl font-semibold tracking-tight text-slate-900 mb-2">My Comprehension Management</h2>
                {teacherLoading && <p className="loading-message full-width text-slate-600">Loading comprehensions...</p>}
                {teacherError && <p className="error-message full-width rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">Error: {teacherError}</p>}
 
                {/* Panel for Teacher's Own Comprehensions */} 
                <div className="comprehension-panel mt-4">
                  <h3 className="text-lg font-medium text-slate-900 mb-2">My Comprehensions</h3>
                  {!teacherLoading && teacherComprehensions.length === 0 ? (
                    <p className="text-slate-600">You haven't created any comprehensions yet.</p>
                  ) : (
                    <div className="comprehension-grid grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {teacherComprehensions.map(comp => (
                        <div key={comp.id} className="comprehension-card teacher-card rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                           {/* Card Content */} 
                           <h4 className="font-medium text-slate-900 mb-1">{comp.title}</h4>
                           <p className="detail text-sm text-slate-600">Created: {new Date(comp.created_at).toLocaleDateString()}</p>
                           
                           <p className="detail submissions text-sm text-slate-700 mt-2">
                             My Student Submissions: <strong className="font-semibold">{comp.student_submission_count}</strong>
                           </p>
                           <div className="mt-3 flex gap-2">
                             <button 
                               onClick={() => handleSelectComprehension(comp)} 
                               className="action-button inline-flex items-center rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-60" 
                               disabled={comp.student_submission_count === 0}
                             >
                                View Submissions
                             </button>
                             <button
                               onClick={() => handleStartTeacherAnswer(comp)}
                               className="action-button try-button inline-flex items-center rounded-md bg-slate-800 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-900" // Add a distinct class if needed
                             >
                                Submit Answer
                             </button>
                           </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
 
                {/* Panel for Public Comprehensions */} 
                <div className="comprehension-panel mt-6">
                  <h3 className="text-lg font-medium text-slate-900 mb-2">Public Comprehensions (Others)</h3>
                  {!teacherLoading && publicComprehensions.length === 0 ? (
                     <p className="text-slate-600">No relevant public comprehensions found.</p>
                  ) : (
                    <div className="comprehension-grid grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {publicComprehensions.map(comp => (
                        <div key={comp.id} className="comprehension-card teacher-card rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                          {/* Card Content */} 
                          <h4 className="font-medium text-slate-900 mb-1">{comp.title}</h4>
                         <p className="detail submissions text-sm text-slate-700">
                            My Student Submissions: <strong className="font-semibold">{comp.student_submission_count}</strong>
                          </p>
                          <div className="mt-3 flex gap-2">
                            <button 
                              onClick={() => handleSelectComprehension(comp)} 
                              className="action-button inline-flex items-center rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-60" 
                              disabled={comp.student_submission_count === 0}
                            >
                                View Submissions
                            </button>
                            <button
                              onClick={() => handleStartTeacherAnswer(comp)}
                              className="action-button try-button inline-flex items-center rounded-md bg-slate-800 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-900" // Add a distinct class if needed
                            >
                              Try This
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
            </div>
          );
      }
  }

  // 5. Fallback: If none of the above conditions are met (e.g., student lands here unexpectedly without a valid submissionId)
  logger.warn("ComprehensionResults rendered in an unexpected state", { isLoading, error, submissionData, userData, urlSubmissionId });
  return <div className="error-message">Could not display comprehension results. Please navigate back or contact support.</div>;
};

export default ComprehensionResults; 